import { MapElement, MapSettings } from '../../../types'
import { Point } from './types'
import { Tool } from '../ToolsPanel'
import {
  getElementZIndex,
  updatePolygonMeasurements,
  isPointInLabel,
} from './helpers'
import { calculatePolygonArea } from '../canvas/geometryUtils'
import { getElementAtPoint, getHandleAtPoint } from '../canvas/hitDetection'
import { isLineBasedElement } from '../canvas/geometryUtils'
import { applySnapping } from '../canvas/snapping'
import { snapPointToAngle } from '../canvas/angleSnapping'

const handlePolygonClick = (
  point: Point,
  isPolygonDrawing: boolean,
  polygonVertices: Point[],
  activeTool: Tool,
  mapSettings: MapSettings,
  onAddElement: (element: MapElement) => void,
  setIsPolygonDrawing: (val: boolean) => void,
  setPolygonVertices: (val: Point[]) => void,
) => {
  if (!isPolygonDrawing) {
    // Start polygon drawing
    setIsPolygonDrawing(true)
    setPolygonVertices([point])
  } else {
    // Add vertex or finish polygon
    const CLOSE_THRESHOLD = 10 // pixels

    // Check if clicking near first vertex to close polygon
    if (polygonVertices.length >= 3) {
      const firstVertex = polygonVertices[0]
      const distance = Math.sqrt(
        Math.pow(point.x - firstVertex.x, 2) +
          Math.pow(point.y - firstVertex.y, 2),
      )

      if (distance <= CLOSE_THRESHOLD) {
        // Close the polygon
        const pixelArea = calculatePolygonArea(polygonVertices)
        const realArea = pixelArea / (mapSettings.scale * mapSettings.scale)

        // Calculate bounding box for approximate dimensions display
        let minX = polygonVertices[0].x,
          maxX = polygonVertices[0].x
        let minY = polygonVertices[0].y,
          maxY = polygonVertices[0].y

        polygonVertices.forEach(vertex => {
          minX = Math.min(minX, vertex.x)
          maxX = Math.max(maxX, vertex.x)
          minY = Math.min(minY, vertex.y)
          maxY = Math.max(maxY, vertex.y)
        })

        const pixelWidth = maxX - minX
        const pixelHeight = maxY - minY
        const realWidth = pixelWidth / mapSettings.scale
        const realHeight = pixelHeight / mapSettings.scale

        const isFixture = activeTool === 'fixture-polygon'
        const newElement: MapElement = {
          id: Date.now().toString(),
          type: isFixture ? 'fixture-polygon' : 'polygon',
          position: polygonVertices[0], // Use first vertex as reference position
          properties: {
            vertices: [...polygonVertices],
            color: isFixture ? '#27ae60' : '#4a90e2',
            strokeWidth: 2,
            fillColor: isFixture
              ? 'rgba(39, 174, 96, 0.2)'
              : 'rgba(200, 220, 255, 0.3)',
            label: isFixture ? 'Fixture Area' : 'Polygon Area', // Explicitly set default
            ...(isFixture
              ? {
                  fixtureCategory: 'polygon',
                  fixtureType: 'area',
                  material: 'composite', // Explicitly set default
                }
              : {
                  roomName: 'Room',
                  roomType: 'general', // Explicitly set default
                  floorMaterial: 'hardwood', // Explicitly set default
                }),
          },
          measurements: {
            realWidth: realWidth,
            realLength: realHeight,
            realArea: realArea,
          },
          floor: mapSettings.currentFloor,
          zIndex: getElementZIndex(isFixture ? 'fixture-polygon' : 'polygon'),
        }
        onAddElement(newElement)

        // Reset polygon drawing
        setIsPolygonDrawing(false)
        setPolygonVertices([])
        return true
      }
    }

    // Add new vertex
    setPolygonVertices([...polygonVertices, point])
    return false
  }
}

const handleTextToolClick = (
  point: Point,
  mapSettings: MapSettings,
  onAddElement: (element: MapElement) => void,
) => {
  const newElement: MapElement = {
    id: Date.now().toString(),
    type: 'text',
    position: point,
    properties: {
      text: 'Label',
      fontSize: 16,
      color: '#333',
      fontFamily: 'Arial', // Explicitly set default
      fontWeight: 'normal', // Explicitly set default
      textAlign: 'center', // Explicitly set default
    },
    floor: mapSettings.currentFloor,
    zIndex: getElementZIndex('text'),
  }
  onAddElement(newElement)
}

const handlePoiToolClick = (
  point: Point,
  mapSettings: MapSettings,
  onAddElement: (element: MapElement) => void,
) => {
  const newElement: MapElement = {
    id: Date.now().toString(),
    type: 'poi',
    position: point,
    properties: {
      label: 'Point of Interest',
      color: '#e74c3c',
      poiType: 'general', // Explicitly set default
      description: '', // Explicitly set default
      icon: 'pin', // Explicitly set default
      size: 20, // Explicitly set default size
    },
    floor: mapSettings.currentFloor,
    zIndex: getElementZIndex('poi'),
  }
  onAddElement(newElement)
}

const handleSelectToolClick = (
  point: Point,
  elements: MapElement[],
  mapSettings: MapSettings,
  onSelectElement: (element: MapElement | null) => void,
  setHoveredVertexIndex: (val: number) => void,
  hoveredVertexIndex: number,
  setDragStart: (val: Point) => void,
  setIsDragging: (val: boolean) => void,
  setDragElementId: (val: string) => void,
  setDragHandle: (
    val:
      | 'start'
      | 'end'
      | 'whole'
      | 'vertex'
      | 'label'
      | 'controlPoint1'
      | 'controlPoint2'
      | null,
  ) => void,
  setDragVertexIndex: (val: number) => void,
) => {
  // First check if we clicked on a label
  let clickedHandle:
    | 'start'
    | 'end'
    | 'vertex'
    | 'label'
    | 'controlPoint1'
    | 'controlPoint2'
    | null = null
  let handleElement: MapElement | null = null

  for (const element of elements) {
    // Check for label clicks on elements with labels
    const hasLabel =
      element.properties.roomName ||
      element.properties.label ||
      (element.type === 'fixture-rect' && element.properties.fixtureType)
    if (hasLabel && isPointInLabel(point, element)) {
      clickedHandle = 'label'
      handleElement = element
      break
    }

    // Check for other handles on line-based elements
    if (isLineBasedElement(element)) {
      const handle = getHandleAtPoint(point, element, setHoveredVertexIndex)
      if (handle) {
        clickedHandle = handle
        handleElement = element
        if (
          handle === 'vertex' ||
          handle === 'controlPoint1' ||
          handle === 'controlPoint2'
        ) {
          setDragVertexIndex(hoveredVertexIndex)
        }
        break
      }
    }
  }

  if (
    clickedHandle &&
    handleElement &&
    mapSettings.allowDragging &&
    !handleElement.locked
  ) {
    // Handle dragging
    onSelectElement(handleElement)
    setIsDragging(true)
    setDragStart(point)
    setDragElementId(handleElement.id)
    setDragHandle(clickedHandle)
    return true
  }

  // If no handle clicked, check for regular element selection
  const clickedElement = getElementAtPoint(point, elements, mapSettings)
  onSelectElement(clickedElement)

  // Start dragging if we clicked on an element and dragging is enabled
  if (clickedElement && mapSettings.allowDragging && !clickedElement.locked) {
    setIsDragging(true)
    setDragStart(point)
    setDragElementId(clickedElement.id)
    setDragHandle(isLineBasedElement(clickedElement) ? 'whole' : null)
    return true
  }

  return false
}

const handleMeasureToolClick = (
  point: Point,
  measureStart: Point | null,
  measureEnd: Point | null,
  mapSettings: MapSettings,
  setMeasureStart: (val: Point | null) => void,
  setMeasureEnd: (val: Point | null) => void,
  setIsDrawing: (val: boolean) => void,
  setDrawStart: (val: Point | null) => void,
  onAddElement: (element: MapElement) => void,
) => {
  if (!measureStart) {
    // Start measuring
    setMeasureStart(point)
    setIsDrawing(true)
    setDrawStart(point)
  } else {
    // Finish measuring and create measurement element
    const distance = Math.sqrt(
      Math.pow(point.x - measureStart.x, 2) +
        Math.pow(point.y - measureStart.y, 2),
    )
    const realDistance = distance / mapSettings.scale

    const newElement: MapElement = {
      id: Date.now().toString(),
      type: 'measurement',
      position: measureStart,
      properties: {
        endPosition: point,
        distance: realDistance,
        showDistance: true,
        strokeColor: '#ff6b6b',
        strokeWidth: 2,
        fontSize: 14,
        dashPattern: [5, 5],
      },
      floor: mapSettings.currentFloor,
      zIndex: 500, // High z-index for measurements
    }
    onAddElement(newElement)

    // Reset measurement state
    setMeasureEnd(null)
    setMeasureStart(null)
    setIsDrawing(false)
    setDrawStart(null)
  }
}

const handleDragMove = (
  point: Point,
  dragStart: Point,
  dragElementId: string,
  dragHandle:
    | 'start'
    | 'end'
    | 'whole'
    | 'vertex'
    | 'label'
    | 'controlPoint1'
    | 'controlPoint2'
    | null,
  dragVertexIndex: number,
  elements: MapElement[],
  mapSettings: MapSettings,
  onUpdateElement: (elementId: string, updates: Partial<MapElement>) => void,
  setDragStart: (val: Point) => void,
  isShiftPressed: boolean,
) => {
  const dragElement = elements.find(el => el.id === dragElementId)
  if (!dragElement) return

  // For whole element dragging, we need to snap based on the element's position
  let snappedPoint: Point
  let deltaX: number
  let deltaY: number

  if (dragHandle === 'whole' || dragHandle === 'label' || !dragHandle) {
    // For whole element/label dragging, calculate where the element would be
    const rawDeltaX = point.x - dragStart.x
    const rawDeltaY = point.y - dragStart.y
    const targetPosition = {
      x: dragElement.position.x + rawDeltaX,
      y: dragElement.position.y + rawDeltaY
    }

    // Apply snapping to the target position
    if (isShiftPressed) {
      const snappedPosition = snapPointToAngle(dragElement.position, targetPosition)
      deltaX = snappedPosition.x - dragElement.position.x
      deltaY = snappedPosition.y - dragElement.position.y
      snappedPoint = { x: dragStart.x + deltaX, y: dragStart.y + deltaY }
    } else {
      const snappedPosition = applySnapping(targetPosition, elements.filter(el => el.id !== dragElementId), mapSettings)
      deltaX = snappedPosition.x - dragElement.position.x
      deltaY = snappedPosition.y - dragElement.position.y
      snappedPoint = { x: dragStart.x + deltaX, y: dragStart.y + deltaY }
    }
  } else {
    // For vertex/handle dragging, snap the actual point
    if (dragHandle === 'start' || dragHandle === 'end') {
      // For line endpoint dragging, snap relative to the opposite endpoint
      const pivotPoint = dragHandle === 'start'
        ? dragElement.properties.endPosition
        : dragElement.position

      if (isShiftPressed && pivotPoint) {
        // Snap to 15-degree angles from the pivot point
        snappedPoint = snapPointToAngle(pivotPoint as Point, point)
      } else {
        // No snapping when shift is not held for line endpoints
        snappedPoint = point
      }
    } else {
      // For other handles (vertex, control points, etc.)
      if (isShiftPressed) {
        snappedPoint = snapPointToAngle(dragStart, point)
      } else {
        snappedPoint = applySnapping(point, elements.filter(el => el.id !== dragElementId), mapSettings)
      }
    }
    deltaX = snappedPoint.x - dragStart.x
    deltaY = snappedPoint.y - dragStart.y
  }

  if (dragHandle === 'vertex' && dragVertexIndex >= 0) {
    // Dragging a polygon vertex
    if (
      (dragElement.type === 'polygon' ||
        dragElement.type === 'fixture-polygon') &&
      dragElement.properties.vertices
    ) {
      const vertices = (dragElement.properties.vertices as Point[]).map(
        (vertex, index) => {
          if (index === dragVertexIndex) {
            // Use the snapped point directly for the vertex
            return snappedPoint
          }
          return vertex
        },
      )

      // Recalculate measurements with updated vertices
      const measurements = updatePolygonMeasurements(
        vertices,
        mapSettings,
        dragElement.curvedEdges,
      )

      onUpdateElement(dragElementId, {
        properties: { ...dragElement.properties, vertices },
        measurements: {
          ...dragElement.measurements,
          ...measurements,
        },
      })
    }
    // Handle rectangle/stairs/room corner dragging
    else if (
      (dragElement.type === 'rectangle' ||
        dragElement.type === 'stairs' ||
        dragElement.type === 'room' ||
        dragElement.type === 'fixture-rect') &&
      dragElement.properties.width &&
      dragElement.properties.height
    ) {
      // Use the snapped point for corner dragging
      let newPosition = { ...dragElement.position }
      let newWidth = dragElement.properties.width
      let newHeight = dragElement.properties.height

      // Handle each corner
      switch (dragVertexIndex) {
        case 0: // top-left
          newWidth =
            dragElement.position.x + dragElement.properties.width - snappedPoint.x
          newHeight =
            dragElement.position.y + dragElement.properties.height - snappedPoint.y
          newPosition = snappedPoint
          break
        case 1: // top-right
          newWidth = snappedPoint.x - dragElement.position.x
          newHeight =
            dragElement.position.y + dragElement.properties.height - snappedPoint.y
          newPosition = { x: dragElement.position.x, y: snappedPoint.y }
          break
        case 2: // bottom-right
          newWidth = snappedPoint.x - dragElement.position.x
          newHeight = snappedPoint.y - dragElement.position.y
          break
        case 3: // bottom-left
          newWidth =
            dragElement.position.x + dragElement.properties.width - snappedPoint.x
          newHeight = snappedPoint.y - dragElement.position.y
          newPosition = { x: snappedPoint.x, y: dragElement.position.y }
          break
      }

      // Ensure minimum size
      newWidth = Math.max(10, newWidth)
      newHeight = Math.max(10, newHeight)

      // Calculate real measurements for the new size
      const pixelArea = newWidth * newHeight
      const realWidth = newWidth / mapSettings.scale
      const realHeight = newHeight / mapSettings.scale
      const realArea = pixelArea / (mapSettings.scale * mapSettings.scale)

      onUpdateElement(dragElementId, {
        position: newPosition,
        properties: {
          ...dragElement.properties,
          width: newWidth,
          height: newHeight,
        },
        measurements: {
          ...dragElement.measurements,
          realWidth,
          realHeight,
          realArea,
        },
      })
    }
    // Handle circle resize by dragging edge
    else if (
      (dragElement.type === 'circle' ||
        dragElement.type === 'fixture-circle') &&
      dragElement.properties.radius
    ) {
      const center = {
        x: dragElement.position.x + dragElement.properties.radius,
        y: dragElement.position.y + dragElement.properties.radius,
      }
      // Use snapped point to calculate new radius
      const newRadius = Math.sqrt(
        Math.pow(snappedPoint.x - center.x, 2) + Math.pow(snappedPoint.y - center.y, 2),
      )
      const radiusDelta = newRadius - dragElement.properties.radius

      // Calculate real measurements for the new radius
      const finalRadius = Math.max(5, newRadius)
      const pixelArea = Math.PI * finalRadius * finalRadius
      const realDiameter = (finalRadius * 2) / mapSettings.scale
      const realArea = pixelArea / (mapSettings.scale * mapSettings.scale)

      // Update position to keep circle centered
      onUpdateElement(dragElementId, {
        position: {
          x: dragElement.position.x - radiusDelta,
          y: dragElement.position.y - radiusDelta,
        },
        properties: {
          ...dragElement.properties,
          radius: finalRadius,
        },
        measurements: {
          ...dragElement.measurements,
          realWidth: realDiameter,  // Use width to represent diameter
          realHeight: realDiameter, // Height equals width for circles
          realArea,
        },
      })
    }
  } else if (dragHandle === 'controlPoint1' || dragHandle === 'controlPoint2') {
    // Handle bezier control point dragging
    const curvedEdges = dragElement.curvedEdges || []
    const edgeIndex = Math.floor(dragVertexIndex / 2)
    const edge = curvedEdges.find(e => e.edgeIndex === edgeIndex)

    if (!edge) return

    const newCurvedEdges = curvedEdges.map(e => {
      if (e.edgeIndex === edgeIndex) {
        if (dragHandle === 'controlPoint1') {
          return {
            ...e,
            controlPoint1: {
              x: e.controlPoint1.x + deltaX,
              y: e.controlPoint1.y + deltaY,
            },
          }
        } else {
          return {
            ...e,
            controlPoint2: {
              x: e.controlPoint2.x + deltaX,
              y: e.controlPoint2.y + deltaY,
            },
          }
        }
      }
      return e
    })

    // Recalculate measurements with updated curved edges
    const vertices = dragElement.properties.vertices as Point[]
    const measurements = updatePolygonMeasurements(
      vertices,
      mapSettings,
      newCurvedEdges,
    )

    onUpdateElement(dragElementId, {
      curvedEdges: newCurvedEdges,
      measurements: {
        ...dragElement.measurements,
        ...measurements,
      },
    })
  } else if (dragHandle === 'label') {
    // Dragging label - update label position offset
    const currentOffset = dragElement.labelPosition || { x: 0, y: 0 }
    const newOffset = {
      x: currentOffset.x + deltaX,
      y: currentOffset.y + deltaY,
    }

    onUpdateElement(dragElementId, {
      labelPosition: newOffset,
    })
  } else if (dragHandle === 'start' && isLineBasedElement(dragElement)) {
    // Dragging start handle - use snapped position directly
    const newPosition = snappedPoint

    // Recalculate distance for measurement elements
    if (
      dragElement.type === 'measurement' &&
      dragElement.properties.endPosition
    ) {
      const distance = Math.sqrt(
        Math.pow(dragElement.properties.endPosition.x - newPosition.x, 2) +
          Math.pow(dragElement.properties.endPosition.y - newPosition.y, 2),
      )
      const realDistance = distance / mapSettings.scale
      onUpdateElement(dragElementId, {
        position: newPosition,
        properties: { ...dragElement.properties, distance: realDistance },
      })
    } else {
      onUpdateElement(dragElementId, { position: newPosition })
    }
  } else if (
    dragHandle === 'end' &&
    isLineBasedElement(dragElement) &&
    dragElement.properties.endPosition
  ) {
    // Dragging end handle - use snapped position directly
    const newEndPosition = snappedPoint

    // Recalculate distance for measurement elements
    if (dragElement.type === 'measurement') {
      const distance = Math.sqrt(
        Math.pow(newEndPosition.x - dragElement.position.x, 2) +
          Math.pow(newEndPosition.y - dragElement.position.y, 2),
      )
      const realDistance = distance / mapSettings.scale
      onUpdateElement(dragElementId, {
        properties: {
          ...dragElement.properties,
          endPosition: newEndPosition,
          distance: realDistance,
        },
      })
    } else {
      onUpdateElement(dragElementId, {
        properties: { ...dragElement.properties, endPosition: newEndPosition },
      })
    }
  } else if (dragHandle === 'whole' && isLineBasedElement(dragElement)) {
    // Dragging whole line element - move both endpoints
    if (
      (dragElement.type === 'polygon' ||
        dragElement.type === 'fixture-polygon') &&
      dragElement.properties.vertices
    ) {
      // Move all polygon vertices
      const vertices = (dragElement.properties.vertices as Point[]).map(
        vertex => ({
          x: vertex.x + deltaX,
          y: vertex.y + deltaY,
        }),
      )
      const newPosition = {
        x: dragElement.position.x + deltaX,
        y: dragElement.position.y + deltaY,
      }

      // Also move curved edges control points if they exist
      const newCurvedEdges = dragElement.curvedEdges
        ? dragElement.curvedEdges.map(curvedEdge => ({
            ...curvedEdge,
            controlPoint1: {
              x: curvedEdge.controlPoint1.x + deltaX,
              y: curvedEdge.controlPoint1.y + deltaY,
            },
            controlPoint2: {
              x: curvedEdge.controlPoint2.x + deltaX,
              y: curvedEdge.controlPoint2.y + deltaY,
            },
          }))
        : undefined

      onUpdateElement(dragElementId, {
        position: newPosition,
        properties: { ...dragElement.properties, vertices },
        ...(newCurvedEdges && { curvedEdges: newCurvedEdges }),
      })
    } else {
      // Regular line-based element
      const newPosition = {
        x: dragElement.position.x + deltaX,
        y: dragElement.position.y + deltaY,
      }
      const newEndPosition = dragElement.properties.endPosition
        ? {
            x: dragElement.properties.endPosition.x + deltaX,
            y: dragElement.properties.endPosition.y + deltaY,
          }
        : undefined

      const updates: Partial<MapElement> = { position: newPosition }
      if (newEndPosition) {
        updates.properties = {
          ...dragElement.properties,
          endPosition: newEndPosition,
        }
      }
      onUpdateElement(dragElementId, updates)
    }
  } else {
    // Regular element dragging (non-line elements)
    const newPosition = {
      x: dragElement.position.x + deltaX,
      y: dragElement.position.y + deltaY,
    }
    onUpdateElement(dragElementId, { position: newPosition })
  }

  setDragStart(snappedPoint)
}

const handleZoomIn = (zoom: number, setZoom: (val: number) => void) => {
  setZoom(Math.min(zoom * 1.2, 5))
}

const handleZoomOut = (zoom: number, setZoom: (val: number) => void) => {
  setZoom(Math.max(zoom / 1.2, 0.2))
}

const addVertex = (
  elementId: string,
  afterIndex: number,
  newVertex: Point,
  elements: MapElement[],
  mapSettings: MapSettings,
  onUpdateElement: (elementId: string, updates: Partial<MapElement>) => void,
  setContextMenu: (val: null) => void,
) => {
  const element = elements.find(el => el.id === elementId)
  if (!element || !element.properties.vertices) return

  const vertices = element.properties.vertices as Point[]
  const newVertices = [
    ...vertices.slice(0, afterIndex + 1),
    newVertex,
    ...vertices.slice(afterIndex + 1),
  ]

  // Recalculate measurements with new vertices
  const measurements = updatePolygonMeasurements(
    newVertices,
    mapSettings,
    element.curvedEdges,
  )

  onUpdateElement(elementId, {
    properties: { ...element.properties, vertices: newVertices },
    measurements: {
      ...element.measurements,
      ...measurements,
    },
  })
  setContextMenu(null)
}

const removeVertex = (
  elementId: string,
  vertexIndex: number,
  elements: MapElement[],
  mapSettings: MapSettings,
  onUpdateElement: (elementId: string, updates: Partial<MapElement>) => void,
  setContextMenu: (val: null) => void,
) => {
  const element = elements.find(el => el.id === elementId)
  if (!element || !element.properties.vertices) return

  const vertices = element.properties.vertices as Point[]

  // Don't allow removing vertices if we'd have less than 3
  if (vertices.length <= 3) return

  const newVertices = vertices.filter((_, index) => index !== vertexIndex)

  // Recalculate measurements with removed vertex
  const measurements = updatePolygonMeasurements(
    newVertices,
    mapSettings,
    element.curvedEdges,
  )

  onUpdateElement(elementId, {
    properties: { ...element.properties, vertices: newVertices },
    measurements: {
      ...element.measurements,
      ...measurements,
    },
  })
  setContextMenu(null)
}

export {
  handlePolygonClick,
  handleTextToolClick,
  handlePoiToolClick,
  handleSelectToolClick,
  handleMeasureToolClick,
  handleDragMove,
  handleZoomIn,
  handleZoomOut,
  addVertex,
  removeVertex,
}
